import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, DollarSign, Globe, Award, FileText, BarChart3, PieChart, Activity, Calendar, Building2, UserCircle, Filter, Download, ListOrdered } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { analyticsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import { toast } from 'sonner';

const Analytics = () => {
  const { pointsSystemEnabled } = useTier();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [quotesActivity, setQuotesActivity] = useState([]);
  const [dealsByRegion, setDealsByRegion] = useState([]);
  const [topPartners, setTopPartners] = useState([]);
  const [quoteTypes, setQuoteTypes] = useState([]);
  const [eventStats, setEventStats] = useState(null);
  const [viewMode, setViewMode] = useState('company'); // 'company' or 'individual'
  const [dateRange, setDateRange] = useState('6m'); // '1m', '3m', '6m', '1y', 'all'
  const [teamAnalytics, setTeamAnalytics] = useState([]);
  const [quotationByStep, setQuotationByStep] = useState({ step_labels: [], rows: [] });

  const { member } = useAuth();
  const currentMemberId = member?.member_id || 'MEM-001-US-GF';

  useEffect(() => {
    fetchData();
  }, [currentMemberId, viewMode, dateRange]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const months = dateRange === '1m' ? 1 : dateRange === '3m' ? 3 : dateRange === '1y' ? 12 : 6;

      const scope = viewMode === 'company' ? 'company' : 'individual';

      if (viewMode === 'company') {
        // Company view: real company overview + team performance
        const [companyRes, stepsRes, activityRes, regionsRes, partnersRes, typesRes, eventsRes] = await Promise.all([
          analyticsApi.getCompanyOverview(currentMemberId),
          analyticsApi.getQuotationByStep(currentMemberId, scope).catch(() => ({ data: { step_labels: [], rows: [] } })),
          analyticsApi.getQuotesActivity(currentMemberId, months),
          analyticsApi.getDealsByRegion(currentMemberId),
          analyticsApi.getTopPartners(currentMemberId, 5),
          analyticsApi.getQuoteTypes(currentMemberId),
          analyticsApi.getEventParticipation(currentMemberId)
        ]);
        setQuotationByStep({
          step_labels: stepsRes?.data?.step_labels || [],
          rows: stepsRes?.data?.rows || [],
        });
        const co = companyRes.data;
        setOverview(co.overview || {});
        setTeamAnalytics((co.team_performance || []).map(m => ({
          name: m.name,
          role: m.role,
          quotes_sent: m.quotes_sent ?? 0,
          deals_won: m.deals_won ?? 0,
          revenue_won: m.revenue_won ?? 0,
          response_rate: m.response_rate ?? 12,
          response_time_display: m.response_time_display || '12 hours'
        })));
        setQuotesActivity(activityRes.data.monthly_activity || []);
        setDealsByRegion(regionsRes.data.deals_by_region || []);
        setTopPartners(partnersRes.data.top_partners || []);
        setQuoteTypes(typesRes.data.quote_types || []);
        setEventStats(eventsRes.data.event_participation || null);
      } else {
        // Individual view: logged-in user's own analytics only
        const [overviewRes, stepsRes, activityRes, regionsRes, partnersRes, typesRes, eventsRes] = await Promise.all([
          analyticsApi.getOverview(currentMemberId),
          analyticsApi.getQuotationByStep(currentMemberId, scope).catch(() => ({ data: { step_labels: [], rows: [] } })),
          analyticsApi.getQuotesActivity(currentMemberId, months),
          analyticsApi.getDealsByRegion(currentMemberId),
          analyticsApi.getTopPartners(currentMemberId, 5),
          analyticsApi.getQuoteTypes(currentMemberId),
          analyticsApi.getEventParticipation(currentMemberId)
        ]);
        setQuotationByStep({
          step_labels: stepsRes?.data?.step_labels || [],
          rows: stepsRes?.data?.rows || [],
        });
        setOverview(overviewRes.data.overview || {});
        setTeamAnalytics([]);
        setQuotesActivity(activityRes.data.monthly_activity || []);
        setDealsByRegion(regionsRes.data.deals_by_region || []);
        setTopPartners(partnersRes.data.top_partners || []);
        setQuoteTypes(typesRes.data.quote_types || []);
        setEventStats(eventsRes.data.event_participation || null);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      const status = error.response?.status;
      const msg = error.response?.data?.detail || error.message;
      if (status === 404) {
        toast.error('Analytics unavailable. Your member profile may not be set up yet.');
      } else if (status === 403) {
        toast.error(msg || 'You don’t have access to analytics.');
      } else {
        toast.error('Failed to load analytics. Check that the backend is running on port 8001.');
      }
      setOverview(null);
      setTeamAnalytics([]);
      setQuotationByStep({ step_labels: [], rows: [] });
      setQuotesActivity([]);
      setDealsByRegion([]);
      setTopPartners([]);
      setQuoteTypes([]);
      setEventStats(null);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className={`text-3xl font-bold mt-2 ${color}`}>{value}</p>
            {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-lg ${color.replace('text-', 'bg-').replace('-600', '-100')}`}>
            <Icon className={color} size={24} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderSimpleBarChart = (data, valueKey, labelKey, maxValue) => {
    const max = maxValue || Math.max(...data.map(d => d[valueKey])) || 1;
    return (
      <div className="space-y-3">
        {data.map((item, index) => (
          <div key={index} className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{item[labelKey]}</span>
              <span className="font-medium">{item[valueKey]}</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full transition-all duration-500"
                style={{ width: `${(item[valueKey] / max) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading analytics...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header with View Toggle and Date Filter */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600 mt-1">Track your performance and business insights</p>
        </div>

        <div className="flex items-center gap-4">
          {/* View Mode Toggle */}
          <div className="inline-flex bg-gray-100 rounded-lg p-1">
            <button
              className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'company' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600'
                }`}
              onClick={() => setViewMode('company')}
            >
              <Building2 size={16} className="mr-1.5" />
              Company
            </button>
            <button
              className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'individual' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600'
                }`}
              onClick={() => setViewMode('individual')}
            >
              <UserCircle size={16} className="mr-1.5" />
              Individual
            </button>
          </div>

          {/* Date Range Filter */}
          <select
            className="px-3 py-2 border rounded-md text-sm"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="1m">Last Month</option>
            <option value="3m">Last 3 Months</option>
            <option value="6m">Last 6 Months</option>
            <option value="1y">Last Year</option>
            <option value="all">All Time</option>
          </select>

          <Button variant="outline" size="sm">
            <Download size={16} className="mr-1.5" />
            Export
          </Button>
        </div>
      </div>

      {/* Revenue & Business Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          title="Revenue"
          value={`$${((overview?.revenue ?? overview?.deal_value ?? 0) / 1000).toFixed(1)}K`}
          icon={DollarSign}
          color="text-green-600"
          subtitle="From completed deals"
        />
        <StatCard
          title="Active Deals"
          value={overview?.active_deals ?? overview?.deals_won ?? 0}
          icon={Award}
          color="text-blue-600"
          subtitle="Nominated to me + by me"
        />
        <StatCard
          title="Completed Deals"
          value={overview?.completed_deals ?? 0}
          icon={TrendingUp}
          color="text-emerald-600"
          subtitle="Deals closed"
        />
        <StatCard
          title="Total Quotes"
          value={overview?.total_quotes || 0}
          icon={FileText}
          color="text-purple-600"
          subtitle="Sent + received"
        />
        <StatCard
          title="Win Ratio"
          value={`${overview?.win_ratio ?? overview?.win_rate ?? 0}%`}
          icon={Activity}
          color="text-amber-600"
          subtitle="Nominated / received"
        />
      </div>

      {/* Quotation pipeline: one column per step, one row per team member (company) or login user (individual) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <ListOrdered size={20} className="mr-2 text-indigo-600" />
            Quotations by lifecycle step
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            {viewMode === 'company' ? 'Company enquiries by team member' : 'Your enquiries'} — count currently in each step
          </p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium sticky left-0 bg-gray-50">Team Member</th>
                  {quotationByStep.step_labels?.map((label, i) => (
                    <th key={i} className="px-3 py-3 text-center font-medium whitespace-nowrap">{label}</th>
                  ))}
                  <th className="px-4 py-3 text-center font-medium">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {quotationByStep.rows?.map((row, idx) => (
                  <tr key={row.member_id || idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900 sticky left-0 bg-white">{row.member_name}</td>
                    {['pending', 'active', 'quoted', 'nominated', 'rfq_completed', 'rfq_accepted', 'completed', 'voided'].map((key) => (
                      <td key={key} className="px-3 py-3 text-center">
                        <Badge variant="outline" className="font-mono">{(row[key] ?? 0)}</Badge>
                      </td>
                    ))}
                    <td className="px-4 py-3 text-center font-semibold">{row.total ?? 0}</td>
                  </tr>
                ))}
              </tbody>
              {quotationByStep.rows?.length > 0 && (
                <tfoot className="bg-gray-100 font-semibold">
                  <tr>
                    <td className="px-4 py-3 sticky left-0 bg-gray-100">Total</td>
                    {['pending', 'active', 'quoted', 'nominated', 'rfq_completed', 'rfq_accepted', 'completed', 'voided'].map((key) => (
                      <td key={key} className="px-3 py-3 text-center">
                        {quotationByStep.rows.reduce((sum, r) => sum + (r[key] ?? 0), 0)}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-center">
                      {quotationByStep.rows.reduce((sum, r) => sum + (r.total ?? 0), 0)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
            {(!quotationByStep.rows?.length) && !loading && (
              <p className="text-gray-500 text-center py-6">No quotation data for this view.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Team Performance (Company View) */}
      {viewMode === 'company' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users size={20} className="mr-2 text-blue-600" />
              Team Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Team Member</th>
                    <th className="px-4 py-3 text-left font-medium">Role</th>
                    <th className="px-4 py-3 text-right font-medium">Quotes Sent</th>
                    <th className="px-4 py-3 text-right font-medium">Deals Won</th>
                    <th className="px-4 py-3 text-right font-medium">Revenue Won</th>
                    <th className="px-4 py-3 text-right font-medium">Response Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {teamAnalytics.map((member, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{member.name}</td>
                      <td className="px-4 py-3 text-gray-500">{member.role}</td>
                      <td className="px-4 py-3 text-right">{member.quotes_sent}</td>
                      <td className="px-4 py-3 text-right">
                        <Badge className="bg-green-100 text-green-700">{member.deals_won}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-green-600">
                        ${(member.revenue_won / 1000).toFixed(0)}K
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={member.response_rate <= 12 ? 'text-green-600' : 'text-amber-600'}>
                          {member.response_time_display}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-100 font-semibold">
                  <tr>
                    <td className="px-4 py-3">Total</td>
                    <td className="px-4 py-3"></td>
                    <td className="px-4 py-3 text-right">{teamAnalytics.reduce((sum, m) => sum + m.quotes_sent, 0)}</td>
                    <td className="px-4 py-3 text-right">{teamAnalytics.reduce((sum, m) => sum + m.deals_won, 0)}</td>
                    <td className="px-4 py-3 text-right text-green-600">
                      ${(teamAnalytics.reduce((sum, m) => sum + m.revenue_won, 0) / 1000).toFixed(0)}K
                    </td>
                    <td className="px-4 py-3 text-right">
                      {overview?.response_time_display || '12 hours'}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Quotes"
          value={overview?.total_quotes || 0}
          icon={FileText}
          color="text-blue-600"
          subtitle="Sent + received"
        />
        <StatCard
          title="Active Deals"
          value={overview?.active_deals ?? overview?.deals_won ?? 0}
          icon={Award}
          color="text-green-600"
          subtitle="Nominated to me + by me"
        />
        <StatCard
          title="Win Ratio"
          value={`${overview?.win_ratio ?? overview?.win_rate ?? 0}%`}
          icon={Activity}
          color="text-purple-600"
          subtitle={`${overview?.reviews_count ?? 0} reviews`}
        />
        {pointsSystemEnabled && (
          <StatCard
            title="Reward Points"
            value={(overview?.reward_points || 0).toLocaleString()}
            icon={TrendingUp}
            color="text-amber-600"
            subtitle={overview?.tier}
          />
        )}
      </div>

      {/* Main Content */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">
            <BarChart3 size={16} className="mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="partners">
            <Users size={16} className="mr-2" />
            Partners
          </TabsTrigger>
          <TabsTrigger value="regions">
            <Globe size={16} className="mr-2" />
            Regions
          </TabsTrigger>
          <TabsTrigger value="events">
            <Calendar size={16} className="mr-2" />
            Events
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Monthly Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BarChart3 size={20} className="mr-2 text-blue-600" />
                  Monthly Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                {quotesActivity.length > 0 ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-6 gap-2">
                      {quotesActivity.map((month, index) => {
                        const maxActivity = Math.max(...quotesActivity.map(m => m.total_activity)) || 1;
                        const heightPercent = (month.total_activity / maxActivity) * 100;
                        return (
                          <div key={index} className="flex flex-col items-center">
                            <div className="h-32 w-full flex items-end">
                              <div
                                className="w-full bg-gradient-to-t from-blue-600 to-indigo-500 rounded-t"
                                style={{ height: `${Math.max(heightPercent, 5)}%` }}
                              />
                            </div>
                            <p className="text-xs text-gray-500 mt-2">{month.month.split(' ')[0]}</p>
                            <p className="text-xs font-medium">{month.total_activity}</p>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex items-center justify-center space-x-6 text-sm pt-4 border-t">
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-blue-600 rounded mr-2" />
                        <span>Quotes Created</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-indigo-400 rounded mr-2" />
                        <span>Bids Sent</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No activity data available</p>
                )}
              </CardContent>
            </Card>

            {/* Quote Types */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <PieChart size={20} className="mr-2 text-green-600" />
                  Quote Types
                </CardTitle>
              </CardHeader>
              <CardContent>
                {quoteTypes.length > 0 ? (
                  renderSimpleBarChart(quoteTypes, 'total', 'type')
                ) : (
                  <p className="text-gray-500 text-center py-8">No quote type data available</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="partners" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users size={20} className="mr-2 text-purple-600" />
                Top Trading Partners
              </CardTitle>
            </CardHeader>
            <CardContent>
              {topPartners.length > 0 ? (
                <div className="space-y-4">
                  {topPartners.map((partner, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium">{partner.name}</p>
                          <p className="text-sm text-gray-500">{partner.country}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-blue-600">{partner.deals} deals</p>
                        <Badge variant="outline">{partner.tier}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No partner data available yet. Complete deals to see your top partners!</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="regions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Globe size={20} className="mr-2 text-green-600" />
                Deals by Region
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dealsByRegion.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {dealsByRegion.map((region, index) => (
                    <div key={index} className="p-4 border rounded-lg hover:border-blue-300 transition-colors">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium">{region.region}</span>
                        <Badge variant="outline">{region.percentage}%</Badge>
                      </div>
                      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-green-500 to-emerald-600 rounded-full"
                          style={{ width: `${region.percentage}%` }}
                        />
                      </div>
                      <p className="text-sm text-gray-500 mt-2">{region.deals} deals</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No regional data available yet. Complete deals to see geographic breakdown!</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="events" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Calendar size={20} className="mr-2 text-amber-600" />
                Event Participation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-blue-600">{eventStats?.total_registrations || 0}</p>
                  <p className="text-sm text-gray-600 mt-1">Total Registrations</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-green-600">{eventStats?.events_attended || 0}</p>
                  <p className="text-sm text-gray-600 mt-1">Events Attended</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-purple-600">{eventStats?.upcoming_events || 0}</p>
                  <p className="text-sm text-gray-600 mt-1">Upcoming Events</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg text-center">
                  <p className="text-3xl font-bold text-amber-600">{eventStats?.attendance_rate || 0}%</p>
                  <p className="text-sm text-gray-600 mt-1">Attendance Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Performance Summary */}
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Performance Summary</h3>
              <p className="text-sm text-gray-600 mt-1">
                You're ranked in the top {100 - Math.min(overview?.win_rate || 0, 95)}% of members based on your activity.
              </p>
            </div>
            <div className="text-right">
              <Badge className="bg-blue-600 text-white px-4 py-2 text-lg">
                {(overview?.tier || member?.tier || 'Silver')} Member
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Analytics;
